import React from "react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { trackEvent } from "@/lib/analytics";
import { HOME } from "@/lib/homeContent";
import { TAGLINE } from "@/lib/brand";
import { usePersonalization } from "@/lib/personalization";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";

const HERO_IMG = "/img/home-hero.jpg";

export default function Hero() {
  const { pick } = usePersonalization();
  const { section } = useSiteContent();
  const c = section("hero", HOME.hero);

  const onSecondary = () => {
    trackEvent("guide_cta_click", { placement: "hero" });
    document.getElementById("guide")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      style={{ backgroundColor: "#2B1017" }}
      className="relative min-h-[92vh] w-full overflow-hidden flex items-end"
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_IMG}
          alt="A residential street in Spain in the early morning"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover mte-zoom"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(13,35,27,0.62) 0%, rgba(43,16,23,0.5) 35%, rgba(43,16,23,0.85) 75%, rgba(43,16,23,0.96) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-44 pb-16 md:pb-20">
        <div className="max-w-3xl">
          <p
            data-testid="hero-tagline"
            className="mte-fade-up text-[#B3A17D] text-xl md:text-2xl italic mb-6"
            style={{ fontFamily: "'Noto Serif', Georgia, serif", textShadow: "0 2px 10px rgba(0,0,0,0.45)" }}
          >
            {TAGLINE}
          </p>
          <h1
            data-testid="hero-headline"
            className="mte-fade-up mte-delay-1 text-white text-5xl sm:text-6xl lg:text-[80px] leading-[1.02] tracking-tight"
            style={{ fontFamily: "Kanit, Arial, sans-serif", fontWeight: 600, textShadow: "0 4px 20px rgba(0,0,0,0.3)" }}
          >
            {pick("hero_headline") || c.headline}
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mte-fade-up mte-delay-2 mt-7 max-w-2xl text-white/90 text-lg md:text-xl leading-relaxed"
            style={{ fontFamily: "'Noto Serif', Georgia, serif" }}
          >
            {pick("hero_sub") || c.sub}
          </p>

          <div className="mte-fade-up mte-delay-3 mt-10 flex flex-col sm:flex-row gap-4 sm:gap-5 sm:items-start">
            <div className="flex flex-col items-start gap-2">
              <ScheduleCallButton label={c.cta_primary} source="hero_primary" tone="sand" testId="hero-cta-button" />
              <span data-testid="hero-cta-note" className="text-white/60 text-[11px] tracking-[0.14em] uppercase pl-1">
                {c.cta_primary_note}
              </span>
            </div>
            <button
              data-testid="hero-cta-secondary"
              onClick={onSecondary}
              className="inline-flex items-center justify-center border-2 border-[#B3A17D]/80 text-white px-9 py-4 md:py-5 text-[12px] md:text-[13px] tracking-[0.2em] uppercase font-medium hover:bg-[#B3A17D] hover:text-[#2B1017] transition-colors rounded-none"
              style={{ fontFamily: "Kanit, Arial, sans-serif" }}
            >
              {c.cta_secondary}
            </button>
          </div>
        </div>

        <div className="mte-fade-up mte-delay-4 mt-14 md:mt-20 border-t border-white/15 pt-6 flex items-center justify-between">
          <p className="text-white/60 text-[11px] tracking-[0.04em]" style={{ fontFamily: "Kanit, Arial, sans-serif" }}><span className="uppercase tracking-[0.22em]">Destino Europe</span> · by Espíritu Travel</p>
          <span className="hidden md:block text-white/40 text-[10px] tracking-[0.3em] uppercase">Spain</span>
        </div>
      </div>
    </section>
  );
}
